import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Typography, withStyles, alpha } from '@material-ui/core';
import FillReviewFlow from '../fillReviewFlow';
import CustomButton from '../customButton';
import ConditionalTooltip from '../conditionalTooltip';
import ActionButton from '../actionButton';
import HeaderImage from '../../../assets/images/kadar_header.png';
import { ReactComponent as PointIcon } from '../../../assets/icons/finger_point.svg';
import { ReactComponent as SmileIcon } from '../../../assets/icons/smile.svg';
import { PARAMS } from '../../../constants/pages';
import {
  getUnionOfTwoArrays,
  isArrayEmpty,
  areArrayItemsReordered,
  isArray,
  areValuesChanged,
} from '../../../utility/helpers';
import { prepareSurveyData } from '../../../utility/fillSurveyUtils';
import {
  CREATE_SURVEY_FIELD_NAMES,
  SURVEY_TYPES,
} from '../../../constants/survey';
import { ATTRIBUTES } from '../../../constants/attributes';
import { ACTION_BUTTON_TYPES } from '../actionButton/config';
import { prepareSurveySteps } from '../../pages/fillSurveyPage/config';
import {
  isPreviewAvailable,
  prepareAttributeData,
  getOrganizationSubject,
} from './config';
import { FIELD_TYPES } from '../../../constants/fieldTypes';
import {
  prepareQuestionnairePreviewData,
  getQuestionnaireQuestions,
} from '../../../utility/survey';

const styles = ({ breakpoints, palette: { primary }, spacing }) => ({
  previewOuter: {
    position: 'relative',
    width: '100%',
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'visible',
  },
  previewOuterAttributes: {
    maxHeight: 'calc(100vh - 224px)',
    [breakpoints.up('xLg')]: {
      maxHeight: 'calc(100vh - 236px)',
    },
  },
  main: {
    width: '100%',
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    maxHeight: '100%',
  },
  fillReviewFlow: {
    backgroundColor: primary.white,
    boxSizing: 'border-box',
    boxShadow: `0px 2px 8px ${alpha(primary.black, 0.07)}`,
    padding: spacing(5, 5, 4, 5),
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    [breakpoints.up('xLg')]: {
      padding: spacing(8, 8, 4, 8),
    },
  },
  fillReviewFlowContent: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  header: {
    background: `${primary.white} url(${HeaderImage})  center / cover no-repeat`,
    width: '100%',
    height: 110,
  },
  headerCustom: {
    height: 80,
  },
  emailMessageWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    maxWidth: '100%',
    height: '100%',
  },
  emailMessageWrapperCustom: {
    minHeight: 0,
    overflowY: 'auto',
  },
  emailContent: {
    width: 600,
    minHeight: 200,
  },
  emailContentCustom: {
    width: '100%',
    maxWidth: 600,
    margin: 'auto',
  },
  emailUser: {
    margin: spacing(3, 0),
  },
  emailMessage: {
    maxHeight: 200,
    wordBreak: 'break-word',
    overflowY: 'hidden',
  },
  actionWrapper: {
    display: 'flex',
    justifyContent: 'center',
    borderBottom: `1px solid ${primary.bluish6}`,
    paddingBottom: spacing(5),
  },
  fillSurveyButton: {
    backgroundColor: primary.bluish1,
    border: 'none',
    borderRadius: 3,
    color: primary.white,
    fontFamily: 'ProximaNova-Bold',
    padding: spacing(2.5, 6),
    maxHeight: 36,
    marginTop: spacing(5),
    '&:hover': {
      backgroundColor: primary.bluish1,
      color: primary.white,
    },
    '&:disabled': {
      backgroundColor: primary.bluish6,
      border: `1px solid ${primary.bluish6}`,
    },
  },
  buttonIconWrapper: {
    marginLeft: spacing(1),
    marginRight: 0,
  },
  buttonIcon: {
    height: 16,
    width: 16,
  },
  buttonIconDisabled: {
    '& g path': {
      fill: `${primary.bluish4} !important`,
    },
  },
  emailFooter: {
    textAlign: 'center',
    marginTop: spacing(4),
  },
  smileIcon: {
    display: 'block',
    margin: spacing(5, 'auto', 0, 'auto'),
  },
  previewRestartButton: {
    position: 'absolute',
    right: 0,
    top: -45,
    zIndex: 1,
  },
  previewRestartButtonCustom: {
    position: 'absolute',
    top: spacing(1),
    right: spacing(1),
    zIndex: 1,
  },
});

const {
  ATTRIBUTES: ATTRIBUTES_FIELD,
  CREATED_FOR,
  IS_ANONYMOUS,
  EMAIL_MESSAGE,
  HAS_QUESTIONNAIRE,
  QUESTIONNAIRE,
} = CREATE_SURVEY_FIELD_NAMES;

class SurveyPreview extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      isPreviewActive: false,
      hasPreview: false,
      survey: {},
      review: {},
      surveyStep: 0,
      reviewStep: 0,
    };
  }

  componentDidMount() {
    this.setSurveyPreviewData();
    this.notifyPreviewActiveChange();
  }

  componentDidUpdate(prevProps, prevState) {
    const { values, isQuestionnaire } = this.props;
    const { isPreviewActive } = this.state;
    const isEnps = [SURVEY_TYPES.ENPS, SURVEY_TYPES.NPS].includes(
      values.survey_type
    );
    const customQuestionsChanged =
      isQuestionnaire &&
      JSON.stringify(prevProps.values[QUESTIONNAIRE] || {}) !==
        JSON.stringify(values[QUESTIONNAIRE] || {});

    if (
      prevProps.values[IS_ANONYMOUS] !== values[IS_ANONYMOUS] ||
      prevProps.values[ATTRIBUTES_FIELD].length !==
        values[ATTRIBUTES_FIELD].length ||
      prevProps.values[CREATED_FOR].length !== values[CREATED_FOR].length ||
      prevProps.values[HAS_QUESTIONNAIRE] !== values[HAS_QUESTIONNAIRE] ||
      getQuestionnaireQuestions(prevProps.values[QUESTIONNAIRE]).length !==
        getQuestionnaireQuestions(values[QUESTIONNAIRE]).length ||
      customQuestionsChanged ||
      (!isEnps &&
        !isQuestionnaire &&
        (areValuesChanged(
          prevProps.values[ATTRIBUTES_FIELD],
          values[ATTRIBUTES_FIELD],
          [FIELD_TYPES.HAS_ANSWER_COMMENT]
        ) ||
          getUnionOfTwoArrays(
            prevProps.values[ATTRIBUTES_FIELD] || [],
            values[ATTRIBUTES_FIELD]
          ).length !== values[ATTRIBUTES_FIELD].length)) ||
      getUnionOfTwoArrays(
        prevProps.values[CREATED_FOR] || [],
        values[CREATED_FOR]
      ).length !== values[CREATED_FOR].length ||
      areArrayItemsReordered(
        prevProps.values[ATTRIBUTES_FIELD] || [],
        values[ATTRIBUTES_FIELD] || []
      )
    ) {
      this.setSurveyPreviewData();
    }

    if (
      prevProps.values[EMAIL_MESSAGE] !== values[EMAIL_MESSAGE] &&
      isPreviewActive
    ) {
      this.handleRestart();
    }

    if (prevState.isPreviewActive !== isPreviewActive) {
      this.notifyPreviewActiveChange();
    }
  }

  notifyPreviewActiveChange = () => {
    const { onPreviewActiveChange } = this.props;
    const { isPreviewActive } = this.state;

    if (onPreviewActiveChange) {
      onPreviewActiveChange(isPreviewActive, this.handleRestart);
    }
  };

  setSurveyPreviewData = () => {
    const { values, onClearReview, isQuestionnaire } = this.props;
    const isEnps = [SURVEY_TYPES.ENPS, SURVEY_TYPES.NPS].includes(
      values.survey_type
    );
    const hasPreview = isPreviewAvailable(
      values,
      isQuestionnaire ? values[QUESTIONNAIRE] : undefined
    );
    const questionnaire = isQuestionnaire
      ? prepareQuestionnairePreviewData(values[QUESTIONNAIRE])
      : null;
    const survey = prepareSurveyData({
      ...values,
      attributes: isQuestionnaire ? [questionnaire] : values[ATTRIBUTES_FIELD],
      subjects: !isArrayEmpty(values[CREATED_FOR])
        ? values[CREATED_FOR]
        : getOrganizationSubject(values.company),
      questionnaire,
    });

    this.setState({
      isPreviewActive: false,
      hasPreview: isEnps ? true : hasPreview,
      survey: hasPreview ? survey : {},
      review: {},
      surveyStep: 0,
      reviewStep: 0,
      questionnaire,
    });
    onClearReview?.();
  };

  prepareReview = () => {
    const { values, isQuestionnaire, attribute } = this.props;
    const { questionnaire } = this.state;

    if (isQuestionnaire) {
      return (
        questionnaire || prepareQuestionnairePreviewData(values[QUESTIONNAIRE])
      );
    }

    return attribute;
  };

  handleGetStarted = attributeId => () => {
    const { values, onGetReview, isQuestionnaire } = this.props;
    const { survey } = this.state;

    if (isQuestionnaire) {
      const questionnaire = prepareQuestionnairePreviewData(
        values[QUESTIONNAIRE]
      );

      return this.setState({
        review: prepareAttributeData(questionnaire, survey.subjects, values),
      });
    }

    const params = {
      [PARAMS.EXCLUDE_ARCHIVED_QUESTIONS]: true,
      [PARAMS.SURVEY_ID]: survey.id,
    };

    return onGetReview(attributeId, params).then(() =>
      this.setState({
        review: prepareAttributeData(
          this.props.attribute,
          survey.subjects,
          values
        ),
      })
    );
  };

  handleStepChange = step => {
    const { values, onGetReview, isQuestionnaire } = this.props;
    const { survey, surveyStep } = this.state;

    if (isQuestionnaire) {
      return this.setState({ reviewStep: step });
    }

    const params = {
      [PARAMS.EXCLUDE_ARCHIVED_QUESTIONS]: true,
      [PARAMS.SURVEY_ID]: survey.id,
    };

    if (step === -1) {
      return onGetReview(
        survey[ATTRIBUTES_FIELD][surveyStep - 1].id,
        params
      ).then(() => {
        const attribute = this.prepareReview();
        const hasAdditionalFeedback =
          attribute[ATTRIBUTES.fields.withAdditionalFeedback];
        const questionsCount = attribute.questions.length;

        return this.setState(prevState => ({
          review: prepareAttributeData(attribute, survey.subjects, values),
          surveyStep: prevState.surveyStep - 1,
          reviewStep: hasAdditionalFeedback
            ? questionsCount
            : questionsCount - 1,
        }));
      });
    }

    return this.setState({ reviewStep: step });
  };

  handleSubmit = () => {
    const { values, attribute, onGetReview, isQuestionnaire } = this.props;
    const { survey, surveyStep } = this.state;

    if (isQuestionnaire) {
      return Promise.resolve();
    }

    const params = {
      [PARAMS.EXCLUDE_ARCHIVED_QUESTIONS]: true,
      [PARAMS.SURVEY_ID]: survey.id,
    };

    if (surveyStep + 1 < survey.totalReviews) {
      return onGetReview(
        survey[ATTRIBUTES_FIELD][surveyStep + 1].id,
        params
      ).then(() => {
        this.setState({
          reviewStep: 0,
          surveyStep: surveyStep + 1,
          review: prepareAttributeData(attribute, survey.subjects, values),
        });
      });
    }
  };

  handleOpenSurvey = () => this.setState({ isPreviewActive: true });

  handleRestart = () => {
    const { onClearReview } = this.props;

    return this.setState(
      {
        isPreviewActive: false,
        surveyStep: 0,
        reviewStep: 0,
        review: {},
      },
      () => onClearReview?.()
    );
  };

  renderEmailMessage = () => {
    const { classes, translations, values, isQuestionnaire } = this.props;
    const { hasPreview } = this.state;

    return (
      <div
        className={classNames(classes.emailMessageWrapper, {
          [classes.emailMessageWrapperCustom]: isQuestionnaire,
        })}
      >
        <div
          className={classNames(classes.emailContent, {
            [classes.emailContentCustom]: isQuestionnaire,
          })}
        >
          <div
            className={classNames(classes.header, {
              [classes.headerCustom]: isQuestionnaire,
            })}
          />
          <Typography className={classes.emailUser} variant="h4">
            {translations.user}
          </Typography>
          <Typography className={classes.emailMessage}>
            {values.message}
          </Typography>
          <div className={classes.actionWrapper}>
            <ConditionalTooltip
              message={translations.disabledButtonTooltip}
              addTooltip={!hasPreview}
            >
              <CustomButton
                className={classes.fillSurveyButton}
                classes={{ startIcon: classes.buttonIconWrapper }}
                type="addWithTextRounded"
                endIcon={
                  <PointIcon
                    className={classNames({
                      [classes.buttonIconDisabled]: !hasPreview,
                    })}
                  />
                }
                disabled={!hasPreview}
                onClick={this.handleOpenSurvey}
              >
                {translations.fillSurveyButton}
              </CustomButton>
            </ConditionalTooltip>
          </div>
          <Typography className={classes.emailFooter} variant="subtitle2">
            {translations.emailFooter}
          </Typography>
          <SmileIcon className={classes.smileIcon} />
        </div>
      </div>
    );
  };

  renderPreview = () => {
    const { classes, translations } = this.props;
    const { survey, surveyStep, reviewStep, review } = this.state;
    const isGroupReview =
      !survey?.isSingleSurvey &&
      isArray(review?.subjects) &&
      !isArrayEmpty(review?.subjects);
    const hasAddCommentButton =
      !review?.withTextReplyQuestions &&
      !isGroupReview &&
      review.hasAnswerComment;

    return (
      <div className={classes.fillReviewFlow}>
        <div className={classes.fillReviewFlowContent}>
          <FillReviewFlow
            translations={translations.fillSurvey}
            surveyStep={surveyStep}
            survey={survey}
            review={review}
            steps={prepareSurveySteps(review, !!survey?.questionnaire)}
            activeStep={reviewStep}
            hasAddCommentButton={hasAddCommentButton}
            onStepChange={this.handleStepChange}
            onSubmit={this.handleSubmit}
            onGetStarted={this.handleGetStarted(survey[ATTRIBUTES_FIELD][0].id)}
            isPreview
          />
        </div>
      </div>
    );
  };

  render() {
    const {
      classes,
      className,
      translations,
      isQuestionnaire,
      hideRestartButton,
    } = this.props;
    const { isPreviewActive, hasPreview } = this.state;

    return (
      <div
        className={classNames(classes.previewOuter, className, {
          [classes.previewOuterAttributes]: !isQuestionnaire,
        })}
      >
        {isPreviewActive && !hideRestartButton && (
          <ActionButton
            className={classNames({
              [classes.previewRestartButtonCustom]: isQuestionnaire,
              [classes.previewRestartButton]: !isQuestionnaire,
            })}
            type={ACTION_BUTTON_TYPES.RELOAD}
            tooltipText={translations.restart}
            onClickHandler={this.handleRestart}
          />
        )}
        <div className={classes.main}>
          {hasPreview && isPreviewActive
            ? this.renderPreview()
            : this.renderEmailMessage()}
        </div>
      </div>
    );
  }
}

SurveyPreview.defaultProps = {
  className: null,
  isQuestionnaire: false,
  attribute: {},
  hideRestartButton: false,
  onPreviewActiveChange: null,
  onGetReview: () => Promise.resolve(),
  onClearReview: null,
};

SurveyPreview.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  translations: PropTypes.object.isRequired,
  values: PropTypes.shape({}).isRequired,
  attribute: PropTypes.shape({}),
  onGetReview: PropTypes.func,
  onClearReview: PropTypes.func,
  isQuestionnaire: PropTypes.bool,
  hideRestartButton: PropTypes.bool,
  onPreviewActiveChange: PropTypes.func,
};

export default withStyles(styles)(SurveyPreview);
